import Localize from 'react-intl-universal';

import { compareAsc } from 'date-fns';

import AccessibilityIcon from '@mui/icons-material/Accessibility';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import HandshakeIcon from '@mui/icons-material/Handshake';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import NoteAltIcon from '@mui/icons-material/NoteAlt';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';

import { EVENT_STATUS_MAP } from '@common/Constants.js';
import { BOOKING_PROCESS } from '@common/network';
import { formatDateHHTimeZone } from '@common/Util.jsx';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import { getByPathAndParams } from '@services/BaseApi';
import { getGoogleMapsBaseUrl } from '@services/baseService';

import { ConditionTypes, EVENT_TYPES } from './Constants';

export const getDuration = (seconds) => ({
  dayDiff: Math.floor(seconds / (3600 * 24)),
  hourDiff: Math.floor((seconds % (3600 * 24)) / 3600),
  minDiff: Math.floor((seconds % 3600) / 60)
});

export const getSessionStartTime = (dateTimeStart) => {
  if (!dateTimeStart || !dateTimeStart?.includes(':')) {
    return '';
  }
  const arr = dateTimeStart.split(':');
  const date = new Date();
  date.setHours(parseInt(arr[0]));
  date.setMinutes(parseInt(arr[1]));

  return formatDateHHTimeZone(date);
};

export const hasEventAlreadyStarted = (event, now = new Date()) => {
  if (event?.status?.id !== EVENT_STATUS_MAP.PUBLISHED || !event?.startDateTime) {
    return false;
  }

  return compareAsc(event.startDateTime, now) === -1;
};

export const addStandardPrice = (obj) => {
  if (!obj) {
    return {};
  }

  const tempStandardPrice =
    obj?.conditions?.find((item) => item?.type?.id === ConditionTypes.STANDARD_PRICE) || null;
  return { ...obj, price: tempStandardPrice?.price || 0, standardPrice: tempStandardPrice };
};

export const getCurrency = (list = []) =>
  list[0]?.attendee?.currency?.value || list[0]?.sessionParticipants[0]?.currency?.value || '';

export const tabConfig = {
  information: { icon: <InfoOutlinedIcon />, label: 'information' },
  participants: { icon: <AccessibilityIcon />, label: 'participants' },
  billing: { icon: <ReceiptLongIcon />, label: 'billing' },
  agenda: { icon: <DateRangeOutlinedIcon />, label: 'agenda' },
  attachments: { icon: <AttachFileOutlinedIcon />, label: 'attachments' },
  notes: { icon: <NoteAltIcon />, label: 'notes' },
  consents: { icon: <HandshakeIcon />, label: 'participationDocuments' }
};

const getMimeType = (path) => {
  const mime = path.split('.').pop()?.toLowerCase() || '';
  switch (mime) {
    case 'pdf':
      return 'application/pdf';
    case 'jpg':
    case 'jpeg':
      return 'image/jpeg';
    case 'png':
      return 'image/png';
    case 'doc':
      return 'application/msword';
    case 'docx':
      return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    case 'xls':
      return 'application/vnd.ms-excel';
    case 'xlsx':
      return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
    default:
      return 'application/octet-stream';
  }
};

export const downloadTemplate = async (filename = '', path, type = '') => {
  if (!path) {
    return;
  }

  getByPathAndParams({ path: path, responseType: 'arraybuffer', temApi: true })
    .then((payload) => {
      if (payload?.data) {
        const blob = new Blob([payload.data], { type: type || getMimeType(filename) });
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.setAttribute('download', filename || 'downloaded-file');
        link.style = 'display: none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.info('Invalid or empty response data when download attachment file');
      }
    })
    .catch((error) => console.error(error));
};

export const formatDataToAddressDto = (data) => {
  return {
    street: data?.contactInformation?.street || '',
    number: data?.contactInformation?.number || '',
    postalCode: data?.contactInformation?.postCode || '',
    city: data?.contactInformation?.city || '',
    regionId: data?.contactInformation?.region?.id || null,
    countryId: data?.contactInformation?.country?.id || null,
    typeId: 1
  };
};

export const formatDataForShowInterest = (data) => {
  return {
    titleId: data?.generalInformation?.title,
    academicTitleId: data?.generalInformation?.academicTitle,
    genderId: data?.generalInformation?.gender,
    firstName: data?.generalInformation?.firstName,
    lastName: data?.generalInformation?.lastName,
    customerReference: data?.generalInformation?.reference,
    companyName: data?.generalInformation?.company,
    emailDtos: [{ email: data?.contactInformation?.email, typeId: 2 }],
    phoneDtos: [{ phone: data?.contactInformation?.phone, typeId: 2 }],
    mobileDtos: [{ phone: data?.contactInformation?.mobile, typeId: 3 }],
    nativeLanguageId: data?.generalInformation?.language,
    vatno: data?.generalInformation?.vatNo,
    statusId: 1,
    typeId: 1,
    eventId: data?.eventId,
    addressDtos: [formatDataToAddressDto(data)]
  };
};

export const useShowInterestOptions = () => {
  const [contextData, setContextData] = useGlobalContext();

  const fetchShowInterestOptions = async () => {
    try {
      if (!contextData?.bookingFormData) {
        const { data } = await getByPathAndParams({
          path: BOOKING_PROCESS.GET_FORM_RESOURCES,
          temApi: true
        });

        const title = data?.title?.filter((title) => title?.active);
        const academicTitle = data?.academictitle?.filter((title) => title?.active);

        setContextData((prev) => ({
          ...prev,
          bookingFormData: {
            ...data,
            title: [
              { id: 0, value: Localize.get('App.none'), key: 'NONE', active: true },
              ...(title || [])
            ],
            academicTitle: [...(academicTitle || [])],
            nativelanguage:
              import.meta.env.VITE_CLIENT_BRAND === 'fraunhofer'
                ? data?.nativelanguage.filter((lang) => lang.key === 'DE' || lang.key === 'EN')
                : data?.nativelanguage,
            industrySectorOptions: [
              { id: 0, value: Localize.get('App.none'), key: 'NONE', active: true },
              ...(data?.industrysector || [])
            ]
          }
        }));
      }
    } catch (err) {
      console.error(err);
    }
  };

  return { fetchShowInterestOptions };
};

const buildAddress = (venue) => {
  const { street, streetNumber, city, country, postCode } = venue;
  const address = [];

  if (street) {
    address.push(street);
    if (streetNumber) {
      address.push(streetNumber);
    }
  }
  if (city) {
    address.push(city);
  }
  if (postCode) {
    address.push(postCode);
  }
  if (country) {
    address.push(country);
  }

  return address.length > 0 ? address.join(', ') : null;
};

export const getGoogleMapsUrl = (venue) => {
  const address = buildAddress(venue);

  if (!address) {
    return null;
  }

  return `${getGoogleMapsBaseUrl()}${encodeURIComponent(address)}`;
};

export const getVenues = (event = null) => {
  let venues = [];

  if (event) {
    venues = [...(event?.venues || [])];

    if (event?.type?.key === EVENT_TYPES.WEBINAR || event?.type?.key === EVENT_TYPES.HYBRID) {
      venues.push({
        id: 'online-event',
        name: Localize.get('App.online'),
        street: '',
        streetNumber: '',
        city: '',
        country: '',
        postCode: ''
      });
    }
  }

  return venues;
};

export const sortByKey =
  (key) =>
  (list = []) =>
    [...list].sort((a, b) => (a?.[key] ?? 0) - (b?.[key] ?? 0));
